// @flow
import BaseModel from 'Stores/BaseModel';
import axios from 'axios';
import {
  validateLength,
  validateNumber,
  validateNotNull,
  isInvalid,
  validateRTELength,
} from 'Utils/Validators';
import { formatPrice, priceMap, sanitizeTags } from 'Utils/index';

declare var API_BASE_URL: string;

export default class ServiceModel extends BaseModel {
  id: number = 0;
  categoryId: ?number = null;
  sortIndex: number = 0;
  name: string = '';
  description: string = '';
  scopes: Array<{ id: number, name: String }> = [];
  benefits: Array<{ id: number, name: String }> = [];
  priceType: string = 'FIXED';
  priceText: string = '';
  footnote: string = '';
  price: number = 0;
  hidden: boolean = true;
  custom: boolean = true;
  consumptionText: ?string = null;
  consumptionInfo: ?string = null;
  consumptionInfoCustomDescriptionEnabled: ?boolean = false;
  consumptionInfoCustomDescription: ?string = null;
  modalType: ?string = '';

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);
  }

  valid = () => {
    this.errors = {};

    if (this.hasPrice()) {
      validateNumber(this.errors, 'price', this.price, 0, 5000);
    }

    // wenn eine Fussnote in der Oberfläche eingetragen wurde muss auch der Text eingetragen werden
    // zu dem diese Fussnote gehört, da sonst die Fussnote für sich allein stehen würde
    if (this.footnote.length > 0) {
      validateLength(this.errors, 'priceText', this.priceText, 1, 5000);
    }

    if (this.custom) {
      validateLength(this.errors, 'name', this.name, 1, 80);
      validateRTELength(this.errors, 'description', sanitizeTags(this.description), 1, 1000);
      validateNotNull(this.errors, 'categoryId', this.categoryId);

      const scopeErrors = {};
      const benefitErrors = {};
      for (let index in this.scopes) {
        const scope = this.scopes[index];
        validateRTELength(scopeErrors, `scopes--${scope.id}`, sanitizeTags(scope.name), 0, 500);
      }

      for (let index in this.benefits) {
        const benefit = this.benefits[index];
        validateRTELength(
          benefitErrors,
          `benefits--${benefit.id}`,
          sanitizeTags(benefit.name),
          0,
          500
        );
      }

      if (Object.keys(scopeErrors).length > 0) {
        this.errors['scopes'] = scopeErrors;
      }

      if (Object.keys(benefitErrors).length > 0) {
        this.errors['benefits'] = benefitErrors;
      }
    }

    return !isInvalid(this.errors);
  };

  save = (opts?: any) => {
    const service = this.toJSON();

    if (opts && opts.publish) {
      service.hidden = false;
    }

    if (service.id > 0) {
      // update a hole custom service
      if (service.custom) {
        return axios.put(`${API_BASE_URL}/services/${service.id}/`, service).then((res) => {
          return new ServiceModel(res.data);
        });
      }

      // override price, hidden for a mazda service
      return axios.patch(`${API_BASE_URL}/services/${service.id}/`, {
        price: service.price,
        priceType: service.priceType,
        priceText: service.priceText,
        footnote: service.footnote,
        hidden: service.hidden,
        consumptionInfo: service.consumptionInfo,
        consumptionInfoCustomDescriptionEnabled: service.consumptionInfoCustomDescriptionEnabled,
        consumptionInfoCustomDescription: service.consumptionInfoCustomDescription,
        modalType: service.modalType,
      });
    }

    return axios.post(`${API_BASE_URL}/services/`, service).then((res) => {
      return new ServiceModel(res.data);
    });
  };

  delete = () => {
    return axios.delete(`${API_BASE_URL}/services/${this.id}/`);
  };

  hasPrice = () => {
    return this.priceType !== 'NONE' && this.priceType !== 'FREE' && this.priceType !== 'INCLUDED';
  };

  toListItem = () => {
    return {
      id: this.id,
      index: this.sortIndex,
      headline: this.name,
      hidden: this.hidden,
      subline: this.hasPrice()
        ? `Ihr Preis: ${priceMap.get(this.priceType)}${formatPrice(this.price)}`
        : 'Kein Preis',
      model: this,
    };
  };

  toJSON = () => {
    return {
      id: this.id,
      categoryId: this.categoryId,
      name: this.name,
      description: this.description,
      scopes: this.scopes,
      benefits: this.benefits,
      sortIndex: this.sortIndex,
      priceType: this.priceType,
      price: this.hasPrice() ? this.price : 0,
      priceText: this.priceText,
      footnote: this.footnote,
      hidden: this.hidden,
      custom: this.custom,
      consumptionText: this.consumptionText,
      consumptionInfo: this.consumptionInfo,
      consumptionInfoCustomDescriptionEnabled: this.consumptionInfoCustomDescriptionEnabled,
      consumptionInfoCustomDescription: this.consumptionInfoCustomDescription,
      modalType: this.modalType,
    };
  };
}
